import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  WorkStatus,
  CatJobHistoryItem,
  CatJobHistoryResponse,
  IntegrationStatusResult,
} from "../../../client/types.gen";

type ModelType = "icm" | "rms" | "rms_elt" | "spatialkat";

interface CatModelPollingConfig {
  modelType: ModelType;
  displayName: string;
  reportId: string;
  toast: Toast;
  initiateRequest: (reportId: string) => Promise<{ request_id: string }>;
  checkStatus: (requestId: string) => Promise<IntegrationStatusResult>;
  fetchJobHistory: (reportId: string) => Promise<CatJobHistoryResponse>;
  /** Called when all pending requests complete without failure. */
  onComplete: (completedIds: string[]) => Promise<void>;
  onJobHistoryUpdate: (modelType: ModelType, jobs: CatJobHistoryItem[]) => void;
  /** If true, extract error_detail from failed statuses for the toast. */
  showErrorDetail?: boolean;
}

interface CatModelPollingResult {
  pendingRequestIds: string[];
  setPendingRequestIds: React.Dispatch<React.SetStateAction<string[]>>;
  isInitiateLoading: boolean;
  isPending: boolean;
  handleInitiate: () => Promise<void>;
}

const POLL_INTERVAL_MS = 2000;

type StatusWithId = IntegrationStatusResult & { id: string };

export function useCatModelPolling(
  config: CatModelPollingConfig
): CatModelPollingResult {
  const [pendingRequestIds, setPendingRequestIds] = useState<string[]>([]);
  const [isInitiateLoading, setIsInitiateLoading] = useState(false);

  // Keep callbacks in a ref so the query functions always see the
  // latest without re-keying the queries on every render.
  const configRef = useRef(config);
  configRef.current = config;

  // One status query per pending id. ``refetchInterval`` returns false
  // once the status reports ``is_complete``, which stops polling for
  // that id while leaving its cached data available — no manual
  // re-entry guards required.
  const statusQueries = useQueries({
    queries: pendingRequestIds.map((id) => ({
      queryKey: [config.modelType, "cat-status", id],
      queryFn: async (): Promise<StatusWithId> => {
        const status = await configRef.current.checkStatus(id);
        return { ...status, id };
      },
      refetchInterval: (query: {
        state: { data: StatusWithId | undefined };
      }) => (query.state.data?.is_complete ? false : POLL_INTERVAL_MS),
    })),
  });

  // Pull job-history alongside the status polls so the dashboard stays
  // in sync. Stops when there are no in-flight requests.
  const historyQuery = useQuery({
    queryKey: [config.modelType, "cat-history", config.reportId],
    queryFn: () =>
      configRef.current.fetchJobHistory(configRef.current.reportId),
    refetchInterval: pendingRequestIds.length > 0 ? POLL_INTERVAL_MS : false,
    enabled: pendingRequestIds.length > 0,
  });

  // Forward history updates upward whenever they arrive.
  useEffect(() => {
    if (historyQuery.data?.jobs) {
      configRef.current.onJobHistoryUpdate(
        configRef.current.modelType,
        historyQuery.data.jobs
      );
    }
  }, [historyQuery.data]);

  // useQueries returns a new array on every render; stash the latest
  // data in a ref so the completion effect can read it without taking
  // a dependency on the array reference.
  const statusDataRef = useRef<(StatusWithId | undefined)[]>([]);
  statusDataRef.current = statusQueries.map((q) => q.data);

  // Transitions false→true exactly once per completion cycle: when the
  // last in-flight status reports complete. After ``setPendingRequestIds([])``
  // the queries unmount and it flips back to false.
  const allComplete =
    pendingRequestIds.length > 0 &&
    statusQueries.every((q) => q.data?.is_complete);

  useEffect(() => {
    if (!allComplete) return;
    const c = configRef.current;
    const completed = statusDataRef.current.filter(
      (s): s is StatusWithId => s !== undefined
    );
    const anyFailed = completed.some((s) => s.status === WorkStatus.FAILED);

    const finish = async () => {
      if (!anyFailed) {
        await c.onComplete(completed.map((s) => s.id));
        c.toast.show({
          severity: "success",
          summary: `${c.displayName} Requests Complete`,
          detail: `All ${c.displayName} requests have been processed successfully.`,
        });
      } else {
        let detail = `Some ${c.displayName} requests failed. Check run history for details.`;
        if (c.showErrorDetail) {
          const errorDetail = completed.find(
            (s) => s.status === WorkStatus.FAILED && s.error_detail
          )?.error_detail;
          if (errorDetail) {
            detail = `${c.displayName} modeling failed: ${errorDetail}`;
          }
        }
        c.toast.show({
          severity: "warn",
          summary: `${c.displayName} Requests Completed`,
          detail,
          life: c.showErrorDetail ? 8000 : 3000,
        });
      }
      setPendingRequestIds([]);
    };

    finish();
  }, [allComplete]);

  const handleInitiate = async () => {
    const c = configRef.current;
    setIsInitiateLoading(true);
    try {
      const response = await c.initiateRequest(c.reportId);
      setPendingRequestIds([response.request_id]);

      const history = await c.fetchJobHistory(c.reportId);
      if (history.jobs) {
        c.onJobHistoryUpdate(c.modelType, history.jobs);
      }
    } catch {
      c.toast.show({
        severity: "error",
        summary: `${c.displayName} Request Failed`,
        detail: `There was an error initiating your ${c.displayName} request.`,
      });
    } finally {
      setIsInitiateLoading(false);
    }
  };

  return {
    pendingRequestIds,
    setPendingRequestIds,
    isInitiateLoading,
    isPending: pendingRequestIds.length > 0 || isInitiateLoading,
    handleInitiate,
  };
}
